import { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Image as ImageIcon, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getGallery, createGalleryItem, updateGalleryItem, deleteGalleryItem, uploadFile } from '@/lib/api';

export const GalleryManager = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const fetchGallery = async () => {
    try {
      const response = await getGallery();
      setGallery(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        title: item.title,
        description: item.description,
        category: item.category,
      });
      setImagePreview(item.image_url);
    } else {
      setSelectedItem(null);
      setFormData({
        title: '',
        description: '',
        category: '',
      });
      setImagePreview(null);
    }
    setSelectedImage(null);
    setDialogOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Wybierz plik obrazu');
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.category) {
      toast.error('Wypełnij wszystkie wymagane pola');
      return;
    }

    if (!selectedItem && !selectedImage) {
      toast.error('Dodaj zdjęcie');
      return;
    }

    setSubmitting(true);
    try {
      if (selectedItem) {
        // Update existing item
        let updateData = {
          title: formData.title,
          description: formData.description,
          category: formData.category,
        };

        // If new image selected, upload it first
        if (selectedImage) {
          const uploadFormData = new FormData();
          uploadFormData.append('file', selectedImage);
          const uploadRes = await uploadFile(uploadFormData);
          updateData.image_url = uploadRes.data.url;
        }

        await updateGalleryItem(selectedItem.id, updateData);
        toast.success('Realizacja zaktualizowana');
      } else {
        // Create new item with image
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('category', formData.category);
        formDataToSend.append('image', selectedImage);
        
        await createGalleryItem(formDataToSend);
        toast.success('Realizacja dodana');
      }
      setDialogOpen(false);
      fetchGallery();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Wystąpił błąd');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    
    try {
      await deleteGalleryItem(selectedItem.id);
      toast.success('Element usunięty');
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      fetchGallery();
    } catch (error) {
      toast.error('Wystąpił błąd');
    }
  };

  const openDeleteDialog = (item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="gallery-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Galeria</h1>
          <p className="text-slate-400">Zarządzaj realizacjami wyświetlanymi na stronie</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="h-11 px-5 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600 text-[#0A0F1C] hover:from-cyan-300 hover:to-cyan-500 font-semibold shadow-lg shadow-cyan-500/30"
          data-testid="add-gallery-button"
        >
          <Plus className="w-5 h-5 mr-2" />
          Dodaj realizację
        </Button>
      </div>

      {gallery.length === 0 ? (
        <div className="bg-[#111827] rounded-xl border border-cyan-500/10 px-6 py-12 text-center text-slate-400">
          Brak elementów w galerii. Dodaj pierwszą realizację.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gallery.map((item) => (
            <div
              key={item.id}
              className="bg-[#111827] rounded-xl border border-cyan-500/10 overflow-hidden card-hover group"
              data-testid={`gallery-row-${item.id}`}
            >
              <div className="relative aspect-video bg-[#0A0F1C]">
                {item.image_url ? (
                  <img
                    src={getImageUrl(item.image_url)}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-12 h-12 text-slate-600" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="icon"
                    variant="secondary"
                    onClick={() => handleOpenDialog(item)}
                    className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30"
                    data-testid={`edit-gallery-${item.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="secondary"
                    onClick={() => openDeleteDialog(item)}
                    className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
                    data-testid={`delete-gallery-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="p-4">
                <span className="inline-block px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-medium rounded mb-2">
                  {item.category}
                </span>
                <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-slate-400 line-clamp-2">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg bg-[#111827] border-cyan-500/20" data-testid="gallery-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">
              {selectedItem ? 'Edytuj realizację' : 'Dodaj realizację'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedItem ? 'Zmień dane lub zdjęcie realizacji' : 'Dodaj nową realizację ze zdjęciem'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Zdjęcie {!selectedItem && '*'}
              </label>
              <div 
                className="border-2 border-dashed border-cyan-500/20 rounded-lg p-4 text-center cursor-pointer hover:border-cyan-500/40 transition-colors bg-[#0A0F1C]"
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview.startsWith('data:') ? imagePreview : getImageUrl(imagePreview)}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage(null);
                        setImagePreview(selectedItem?.image_url || null);
                      }}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="text-sm text-slate-400 mt-2">
                      Kliknij aby zmienić zdjęcie
                    </p>
                  </div>
                ) : (
                  <div className="py-8">
                    <Upload className="w-10 h-10 text-cyan-400 mx-auto mb-2" />
                    <p className="text-slate-300">Kliknij aby wybrać zdjęcie</p>
                    <p className="text-xs text-slate-500 mt-1">JPG, PNG, GIF, WEBP</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                data-testid="gallery-image-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Tytuł *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Np. System monitoringu dla firmy"
                className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
                data-testid="gallery-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Kategoria *</label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="Np. Monitoring, Sieci, Wideofony"
                className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
                data-testid="gallery-category-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Opis</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Opis realizacji..."
                rows={3}
                className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
                data-testid="gallery-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-cyan-500/20 text-slate-300 hover:bg-cyan-500/10">
              Anuluj
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-gradient-to-r from-cyan-400 to-cyan-600 text-[#0A0F1C] hover:from-cyan-300 hover:to-cyan-500 font-semibold"
              data-testid="gallery-submit-button"
            >
              {submitting ? 'Zapisywanie...' : selectedItem ? 'Zapisz' : 'Dodaj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md bg-[#111827] border-cyan-500/20" data-testid="delete-gallery-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Usuń realizację</DialogTitle>
            <DialogDescription className="text-slate-400">
              Ta operacja jest nieodwracalna.
            </DialogDescription>
          </DialogHeader>
          <p className="text-slate-400 py-4">
            Czy na pewno chcesz usunąć "{selectedItem?.title}"? 
            Ta operacja jest nieodwracalna.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-cyan-500/20 text-slate-300 hover:bg-cyan-500/10">
              Anuluj
            </Button>
            <Button 
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-gallery-button"
            >
              Usuń
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryManager;
